const HARMONY_OFFSETS = [
  [-38, -19, 0, 19, 38],
  [0, 24, 150, 180, 210],
  [0, 22, 120, 142, 240],
  [0, 34, 180, 214, 300],
  [0, 0, 0, 0, 0]
] as const;

const LIGHTNESS_PROFILES = [
  [24, 38, 52, 68, 84],
  [82, 66, 50, 36, 22],
  [32, 58, 76, 44, 88],
  [72, 42, 26, 56, 84]
] as const;

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function wrapHue(value: number): number {
  return ((value % 360) + 360) % 360;
}

function channelToHex(value: number): string {
  return Math.round(clamp(value, 0, 255)).toString(16).padStart(2, '0');
}

function hslToHex(hue: number, saturation: number, lightness: number): string {
  const h = wrapHue(hue) / 360;
  const s = clamp(saturation, 0, 100) / 100;
  const l = clamp(lightness, 0, 100) / 100;
  const chroma = (1 - Math.abs(2 * l - 1)) * s;
  const sector = h * 6;
  const intermediate = chroma * (1 - Math.abs((sector % 2) - 1));
  const offset = l - chroma / 2;

  let red = 0;
  let green = 0;
  let blue = 0;
  if (sector < 1) {
    red = chroma;
    green = intermediate;
  } else if (sector < 2) {
    red = intermediate;
    green = chroma;
  } else if (sector < 3) {
    green = chroma;
    blue = intermediate;
  } else if (sector < 4) {
    green = intermediate;
    blue = chroma;
  } else if (sector < 5) {
    red = intermediate;
    blue = chroma;
  } else {
    red = chroma;
    blue = intermediate;
  }

  return `#${channelToHex((red + offset) * 255)}${channelToHex((green + offset) * 255)}${channelToHex((blue + offset) * 255)}`;
}

function randomIndex(length: number, random: () => number): number {
  return Math.min(length - 1, Math.floor(clamp(random(), 0, 0.999999) * length));
}

/**
 * Produces five intentionally related colours rather than five independent
 * random values. Hue offsets follow common colour harmonies, while the fixed
 * lightness profiles keep enough contrast for both ramps and mesh points.
 */
export function generateHarmoniousGradientPalette(random: () => number = Math.random): string[] {
  const baseHue = random() * 360;
  const offsets = HARMONY_OFFSETS[randomIndex(HARMONY_OFFSETS.length, random)];
  const lightness = LIGHTNESS_PROFILES[randomIndex(LIGHTNESS_PROFILES.length, random)];
  const baseSaturation = 64 + random() * 22;
  const reverse = random() > 0.5;

  const colors = offsets.map((offset, index) => {
    const saturationVariation = (random() - 0.5) * 12;
    const hueVariation = offsets.every((value) => value === 0) ? 0 : (random() - 0.5) * 7;
    return hslToHex(
      baseHue + offset + hueVariation,
      baseSaturation + saturationVariation,
      lightness[index]
    );
  });

  return reverse ? colors.reverse() : colors;
}
